import {useNotification} from '@hconnect/uikit/src/common/'
import {useMutation} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useCallback} from 'react'
import {useErrorHandler} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'

import {mutations} from '../../../api/mutations'
import {invalidateHcemQuery} from '../../../api/queryHelpers'
import {notifyIfErrorMessage} from '../../../common'
import {PlannerForecastType} from '../../../enums'
import {ErrorResponse} from '../../../interfaces/api/common'
import {RecalculatePlanData} from '../../../interfaces/api/optimizer'
import {mapOptimizerSettingsToDTO} from '../../../selectors/optimizer'
import {useUrlParam} from '../../useUrlParam'

export const useRecalculatePlan = () => {
  const plantCode = useUrlParam('plantCode')
  const {notify} = useNotification()
  const {t} = useTranslation()
  const raiseError = useErrorHandler()
  const {mutate, isPending} = useMutation<
    void,
    AxiosError<ErrorResponse>,
    Parameters<typeof mutations.recalculatePlan>[0]
  >({
    mutationFn: mutations.recalculatePlan,
    onError: (error) => {
      notify('error', t('error.apology'))
      notifyIfErrorMessage(error.response?.data.detail, raiseError, notify)
    },
    onSuccess: () => {
      notify('success', t('success.recalculateOptimizedPlanning'))
    },
    onSettled: (data, error, {plantCode, dto}) => {
      if (dto.save_settings) {
        invalidateHcemQuery('optimizerSettings', plantCode)
      }
      invalidateHcemQuery('optimizerRunsAndSchedules', {plantCode})
    }
  })

  const recalculatePlan = useCallback(
    (data: RecalculatePlanData) => {
      mutate({
        plantCode,
        dto: {
          settings: mapOptimizerSettingsToDTO(data.optimizerSettings, true),
          save_settings: data.saveSettings,
          name: data.name,
          forecast_type: PlannerForecastType.StatisticalForecast
        }
      })
    },
    [plantCode, mutate]
  )

  return {recalculatePlan, isLoading: isPending}
}
